export const LEGAL_DOCUMENT_TITLE = 'TERMS OF USE | PRIVACY POLICY';

export const TERMS_INTRO =
  'Welcome to Echo Jham Entertainment. By creating an account or using the Echo Jham website and its ticketing and event-booking services, you agree to comply with these Terms of Use.';

export const TERMS_SECTIONS = [
  {
    id: 'terms-1',
    title: '1. Use of the Platform',
    paragraphs: [
      'Echo Jham provides online services for viewing events, booking events, purchasing tickets, managing bookings, and accessing related entertainment services.',
      'Users must provide accurate and complete information when creating an account. Echo Jham may require email verification before an account can be used. This is consistent with the system requirement that registered users verify their accounts through an email verification link.',
    ],
  },
  {
    id: 'terms-2',
    title: '2. User Account',
    paragraphs: [
      'Users are responsible for maintaining the confidentiality of their account credentials and for activities performed through their account.',
      'Users must not:',
    ],
    bullets: [
      'provide false or misleading information;',
      "use another person's account without authorization;",
      'attempt to gain unauthorized access to the system;',
      'interfere with the operation or security of the platform; or',
      'use the platform for unlawful or fraudulent activities.',
    ],
    afterBullets: [
      'Echo Jham may restrict or suspend an account when necessary to protect the platform, its users, events, bookings, or ticketing operations.',
    ],
  },
  {
    id: 'terms-3',
    title: '3. Ticketing and Event Bookings',
    paragraphs: [
      'Users are responsible for reviewing event information, ticket details, dates, times, venues, prices, and booking information before completing a transaction.',
      'Once a booking or ticket purchase is submitted, the applicable event, ticket, payment, cancellation, refund, and other transaction rules shall apply.',
      'Users must not reproduce, resell, manipulate, or fraudulently use tickets or booking information.',
    ],
  },
  {
    id: 'terms-4',
    title: '4. Account Verification and Security',
    paragraphs: [
      'Echo Jham may use email verification and other security measures to protect user accounts.',
      'The system is designed to validate registration information, prevent duplicate accounts using the same email address, enforce password requirements, and securely store user credentials.',
    ],
  },
  {
    id: 'terms-5',
    title: '5. Prohibited Activities',
    paragraphs: ['Users shall not use Echo Jham to:'],
    bullets: [
      'commit fraud or facilitate fraudulent transactions;',
      'impersonate another person;',
      'distribute malicious software;',
      'attempt unauthorized access to accounts or system components;',
      'manipulate tickets, bookings, or event information; or',
      'violate applicable Philippine laws and regulations.',
    ],
  },
  {
    id: 'terms-6',
    title: '6. Changes to the Terms',
    paragraphs: [
      'Echo Jham may update these Terms of Use when necessary. Material changes should be communicated to users through appropriate means.',
      'Continued use of the platform after an updated version becomes effective constitutes acceptance of the revised Terms, subject to applicable law.',
    ],
  },
  {
    id: 'terms-7',
    title: '7. Contact',
    paragraphs: [
      'For questions regarding these Terms of Use, users may contact:',
      'Echo Jham Entertainment',
      'echojhamproductions@gmail.com',
    ],
  },
];

export const PRIVACY_INTRO =
  'Echo Jham Entertainment respects the privacy of its users and is committed to protecting personal information in accordance with Republic Act No. 10173, or the Data Privacy Act of 2012, its Implementing Rules and Regulations, and applicable issuances of the National Privacy Commission (NPC).';

export const PRIVACY_SECTIONS = [
  {
    id: 'privacy-1',
    title: '1. Personal Information We Collect',
    paragraphs: [
      'When you register for an Echo Jham account or use our ticketing and booking services, we may collect information such as:',
    ],
    bullets: [
      'Full name',
      'Email address',
      'Account credentials',
      'Booking information',
      'Ticket information',
      'Transaction-related information',
      'Information that you voluntarily provide when contacting or using the platform',
    ],
    afterBullets: [
      "For the registration process specifically, the Echo Jham system requires the user's name, email, and password.",
      "Echo Jham should only collect information that is adequate, relevant, necessary, and not excessive for the stated purpose, consistent with the DPA's principle of proportionality.",
    ],
  },
  {
    id: 'privacy-2',
    title: '2. Why We Collect Your Information',
    paragraphs: [
      'Your personal information may be processed for legitimate and specified purposes, including:',
    ],
    bullets: [
      'creating and managing your Echo Jham account;',
      'verifying your email address;',
      'processing event bookings;',
      'processing ticket purchases;',
      'maintaining booking and ticket records;',
      'providing event and booking-related notifications;',
      'responding to customer inquiries;',
      'maintaining account and platform security;',
      'preventing fraudulent or unauthorized activity; and',
      'complying with applicable legal obligations.',
    ],
    afterBullets: [
      'The DPA recognizes several lawful bases for processing personal information, including consent, fulfillment of a contract, compliance with a legal obligation, protection of vital interests, public authority functions, and legitimate interests, subject to the requirements of the law.',
    ],
  },
  {
    id: 'privacy-3',
    title: '3. Email Verification',
    paragraphs: [
      "Echo Jham uses email verification as part of account registration. After successful registration, the system sends a verification link to the user's registered email address.",
      'Verification links may expire and may not be reused after successful activation.',
    ],
  },
  {
    id: 'privacy-4',
    title: '4. How We Protect Your Information',
    paragraphs: [
      'Echo Jham shall implement reasonable and appropriate organizational, physical, and technical measures to protect personal information against unauthorized access, alteration, disclosure, destruction, or other unlawful processing.',
      'User credentials must be stored securely as required by the system specifications.',
    ],
  },
  {
    id: 'privacy-5',
    title: '5. Data Sharing and Disclosure',
    paragraphs: [
      'Echo Jham shall not disclose personal information to third parties unless the disclosure is:',
    ],
    bullets: [
      'necessary for a legitimate and specified purpose;',
      'authorized or permitted by applicable law;',
      'necessary to provide a requested service;',
      'necessary to fulfill a contractual obligation;',
      'required by a lawful government or legal process; or',
      'otherwise permitted under the Data Privacy Act.',
    ],
    afterBullets: [
      'Where applicable, appropriate safeguards shall be implemented for data sharing and processing.',
    ],
  },
  {
    id: 'privacy-6',
    title: '6. Data Retention',
    paragraphs: [
      'Personal information shall not be retained longer than necessary for the purpose for which it was collected, except where retention is permitted or required by law, necessary for legitimate business purposes, or necessary for the establishment, exercise, or defense of legal claims. This follows Section 11(e) of the DPA and the corresponding IRR provisions.',
    ],
  },
  {
    id: 'privacy-7',
    title: '7. Your Rights as a Data Subject',
    paragraphs: [
      'Under the Data Privacy Act, users have rights relating to their personal information, including the right to:',
    ],
    bullets: [
      'Be informed about the processing of their personal information;',
      'Access their personal information;',
      'Correct or rectify inaccurate or incomplete information;',
      'Object to certain processing;',
      'Request erasure or blocking where applicable;',
      'Data portability, where applicable;',
      'File a complaint with the National Privacy Commission; and',
      'Seek damages where provided by law.',
    ],
    afterBullets: [
      'The DPA expressly recognizes the right to be informed and provides information that should be made available to data subjects, including the personal information being processed, purposes, scope and method of processing, recipients, controller identity/contact details, storage period, and applicable rights.',
    ],
  },
  {
    id: 'privacy-8',
    title: '8. Consent',
    paragraphs: [
      'Where processing is based on consent, consent must be freely given, specific, informed, and evidenced through written, electronic, or recorded means. Consent may also be withdrawn, subject to applicable legal requirements and consequences.',
    ],
  },
];
